import { Dialog, MobileDialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link2, Share2, DollarSign } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "@/hooks/useTranslation";
import { HeadingSmall, BodySmall, Caption } from "@/components/ui/typography";

interface ReferralExplanationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReferralExplanationDialog({ open, onOpenChange }: ReferralExplanationDialogProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('ui');

  const handleApplyClick = () => {
    onOpenChange(false);
    navigate('/apply/recruiter');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <MobileDialogContent className="p-0 flex flex-col overflow-hidden md:max-w-md md:rounded-xl">
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
          <div className="space-y-4">
        <DialogHeader>
          <DialogTitle>{t('referral_dialog.title')}</DialogTitle>
          <DialogDescription>
            {t('referral_dialog.description')}
          </DialogDescription>
        </DialogHeader>
          <div className="space-y-4">
            {/* 3-Step Process */}
            <div className="flex items-center justify-between gap-3 py-2">
            <div className="flex flex-col items-center text-center flex-1">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                <Link2 className="h-5 w-5 text-primary" />
              </div>
              <Caption className="font-medium">{t('referral_dialog.copy_link')}</Caption>
            </div>
            
            <div className="flex flex-col items-center text-center flex-1">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                <Share2 className="h-5 w-5 text-primary" />
              </div>
              <Caption className="font-medium">{t('referral_dialog.share_it')}</Caption>
            </div>
            
            <div className="flex flex-col items-center text-center flex-1">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <Caption className="font-medium">{t('referral_dialog.earn')}</Caption>
            </div>
          </div>

          <Separator />

          {/* How It Works */}
          <div className="space-y-3">
            <HeadingSmall>{t('referral_dialog.how_it_works')}</HeadingSmall>
            <ol className="space-y-2">
              <li className="flex gap-2">
                <span className="font-medium text-foreground">1.</span>
                <BodySmall>{t('referral_dialog.step_1')}</BodySmall>
              </li>
              <li className="flex gap-2">
                <span className="font-medium text-foreground">2.</span>
                <BodySmall>{t('referral_dialog.step_2')}</BodySmall>
              </li>
              <li className="flex gap-2">
                <span className="font-medium text-foreground">3.</span>
                <BodySmall>{t('referral_dialog.step_3')}</BodySmall>
              </li>
            </ol>
            <Caption className="pt-2">
              {t('referral_dialog.commission_note')}
            </Caption>
          </div>

            {/* Action Button */}
            <div className="pt-2">
              <Button onClick={handleApplyClick} className="w-full">
                {t('referral_dialog.apply_button')}
              </Button>
            </div>
          </div>
        </div>
        </div>
      </MobileDialogContent>
    </Dialog>
  );
}
